/**
 * O painel do dado dentro da mesa (E203.F2, ADR-0146 §1–§4): o estado (o pedido aberto, um por vez,
 * e se há alteração não salva) e o painel com o conteúdo da origem. O clique noutro bloco com o
 * painel sujo não troca o painel: ele pergunta antes de descartar.
 *
 * E203.F4.C (F-E203.F4-04): o painel guarda a versão do documento que mostrava ao abrir
 * (`avaliacaoId`); a revisão a leva e o servidor recusa se o documento mudou ou já foi assinado.
 *
 * E205.F3.C03 (F-E205.F3-03): a identidade do painel é a origem inteira, com o referente: o mesmo
 * campo de outra CCB remonta o conteúdo, sem a resposta nem o motivo da anterior.
 */
import type { AxiosInstance } from 'axios';
import { useCallback, useRef, useState } from 'react';

import { PainelDoDado } from '@/components/DocumentoDaMesa/PainelDoDado';
import type { PedidoDoDado } from '@/components/DocumentoDaMesa/pedidoDoDado';

import { ConteudoDoDado } from './ConteudoDoDado';
import type { Mesa } from './tipos';

/** `documento`: o documento que a mesa mostra agora (a versão dele vai com o painel que abre). */
export function usePainelDoDadoDaMesa(documento: { avaliacao_id: string | null } | null = null) {
  const [pedido, setPedido] = useState<PedidoDoDado | null>(null);
  const [avaliacaoId, setAvaliacaoId] = useState<string | null>(null);
  const avaliacaoAgora = useRef<string | null>(null);
  avaliacaoAgora.current = documento?.avaliacao_id ?? null;
  const [sujo, setSujoDoEstado] = useState(false);
  const [insistir, setInsistir] = useState(0);
  const sujoAgora = useRef(false);
  const setSujo = useCallback((valor: boolean) => {
    sujoAgora.current = valor;
    setSujoDoEstado(valor);
  }, []);
  const abrir = useCallback((novo: PedidoDoDado) => {
    if (sujoAgora.current) {
      setInsistir((n) => n + 1);
      return;
    }
    setInsistir(0);
    setPedido(novo);
    setAvaliacaoId(avaliacaoAgora.current);
  }, []);
  const fechar = useCallback(() => {
    setSujo(false);
    setInsistir(0);
    setPedido(null);
  }, [setSujo]);
  return { pedido, avaliacaoId, sujo, insistir, abrir, fechar, setSujo };
}

export type EstadoDoPainel = ReturnType<typeof usePainelDoDadoDaMesa>;

export interface PainelDoDadoDaMesaProps {
  estado: EstadoDoPainel;
  client: AxiosInstance;
  caseId: string;
  mesa: Mesa;
  onMudou: () => void;
  /** A nota do perito da seção, pela chave da seção (`null`: a seção não aceita nota). */
  notaDaSecao: (secao: string | null) => (() => void) | null;
}

export function PainelDoDadoDaMesa({ estado, notaDaSecao, ...props }: PainelDoDadoDaMesaProps) {
  const { pedido } = estado;
  if (pedido === null) return null;
  const { origem } = pedido;
  const chave = [
    origem.tipo,
    origem.chave,
    origem.linha ?? '',
    origem.referente ?? '',
    pedido.impresso,
  ].join(':');
  return (
    <PainelDoDado
      key={chave}
      pedido={pedido}
      onFechar={estado.fechar}
      onSujo={estado.setSujo}
      insistir={estado.insistir}
    >
      {(acoes) => (
        <ConteudoDoDado
          {...props}
          pedido={pedido}
          avaliacaoId={estado.avaliacaoId}
          acoes={acoes}
          abrirNota={notaDaSecao(pedido.secao)}
        />
      )}
    </PainelDoDado>
  );
}
